import { useCallback, useEffect, useMemo, useState } from "react";
import { useProjectParams } from "~/helpers";
import { VolumeCreationResponse } from "~/lib/wss-client";
import { useWebsocket } from "./useWebsocket";

type UseWebSocketCreateVolumeReturn = {
  createVolume: (
    sourceCaseId: string,
    name: string,
    documentIds: string[]
  ) => void;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
};

type UseWebSocketCreateVolumeOptions = {
  onVolumeCreationResponse?: (response: VolumeCreationResponse) => void;
};

export const useWebSocketCreateVolume = (
  options?: UseWebSocketCreateVolumeOptions
): UseWebSocketCreateVolumeReturn => {
  const { organizationId, caseId } = useProjectParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsParams = useMemo(
    () => ({
      projectId: caseId,
      subscribeTo: "volume-creation" as const,
    }),
    [caseId]
  );

  const {
    isConnected,
    addVolumeCreationListener,
    removeVolumeCreationListener,
    sendVolumeCreation,
  } = useWebsocket<VolumeCreationResponse>(wsParams);

  const handleVolumeCreationResponse = useCallback(
    (response: VolumeCreationResponse) => {
      setIsLoading(false);

      if (response.action === "message-error") {
        setError(response.error || "Unknown error occurred");
      } else if (response.action === "message-response") {
        setError(null);
        // Call external handler if provided
        options?.onVolumeCreationResponse?.(response);
      }
    },
    [options]
  );

  useEffect(() => {
    addVolumeCreationListener(handleVolumeCreationResponse);
    return () => {
      removeVolumeCreationListener(handleVolumeCreationResponse);
    };
  }, [
    addVolumeCreationListener,
    removeVolumeCreationListener,
    handleVolumeCreationResponse,
  ]);

  const createVolume = useCallback(
    (sourceCaseId: string, name: string, documentIds: string[]) => {
      if (!isConnected) {
        setError("WebSocket is not connected");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        sendVolumeCreation(organizationId, sourceCaseId, name, documentIds);
      } catch (err) {
        setIsLoading(false);
        setError(
          err instanceof Error ? err.message : "Failed to create volume"
        );
      }
    },
    [isConnected, organizationId, sendVolumeCreation]
  );

  return {
    createVolume,
    isConnected,
    isLoading,
    error,
  };
};
